import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react'
import { filterFns, flexRender, getCoreRowModel, getFilteredRowModel, useReactTable } from '@tanstack/react-table'
import { searchUtils } from '../../../../utils/search.utils'
import Search from '../../../Common/DaisyTable/Search/Search'
import { useCapsuleConfigurator } from '../../../../context/CapsuleConfiguratorContext'
import sortingUtils from '../../../../utils/sorting.utils'
import MixturesRowActions from '../../../Mixtures/RowActions/MixturesRowActions'
import Pagination from '../../../Common/DaisyTable/Pagination/Pagination'
import { includes, isArray } from 'lodash'
import { createColumnsWithCheckbox, getColumsVisibility, getCorrectPageCount } from '../../../../utils/table.utils'
import { formatMixtureForConfiguration, mergeMixturesArray } from '../../../../utils/format.utils'
import LoadingSpinner from '../../../Common/Loading/LoadingSpinner'
import { ArrowDown, ArrowUp } from 'lucide-react'
import ReagentsModal from '../../../Capsules/ReagentsModal'




// Composant Arrow pour les indicateurs de tri
const SortArrow = React.memo(({ direction }) => {
    if (!direction) return null;

    return direction === 'asc' ? (
        <ArrowUp className='w-4 h-4' />
    ) : (
        <ArrowDown className='w-4 h-4' />
    );
});

// Composant pour les en-têtes de colonnes
const TableHeader = React.memo(({ header }) => {
    const direction = header.column.getIsSorted();

    return (
        <th key={header.id} colSpan={header.colSpan} style={{ width: header.getSize() }} className='relative'>
            {header.isPlaceholder ? null : (
                <div
                    onClick={header.column.getToggleSortingHandler()}
                    className="cursor-pointer flex gap-4 items-center"
                >
                    {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                    )}
                    <SortArrow direction={direction} />
                </div>
            )}
            <div
                onDoubleClick={() => header.column.resetSize()}
                onMouseDown={header.getResizeHandler()}
                onTouchStart={header.getResizeHandler()}
                className={`resizer ${header.column.getIsResizing() ? 'isResizing' : ''}`}
            />
        </th>
    );
});

// Composant pour une ligne de tableau
const TableRow = React.memo(({ row, selection, initialSelection, isFirstConfiguration }) => {
    const isSelected = !!selection[row.id];
    const isNewSelection = isSelected && !initialSelection[row.id];
    const isEffluence = row.original.category === 'Effluences';

    let rowClassName = '';

    if (isSelected) {
        if (isFirstConfiguration || isNewSelection) {
            rowClassName += 'bg-gray-300 border-l-4 border-gray-500 ';
        } else {
            rowClassName += 'bg-primary/30 border-l-4 border-primary ';
        }
    }

    return (
        <tr className={rowClassName}>
            {row.getVisibleCells().map((cell, index) => {
                if (cell.column.id === 'select' && index === 0) {
                    return (
                        <td onClick={(e) => e.stopPropagation()} key={cell.id} style={{ width: cell.column.getSize() }}>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                    );
                }
                return (
                    <td key={cell.id} style={{ width: cell.column.getSize() }}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                );
            })}
        </tr>
    );
});

// Composant pour les boutons de catalogue
const CatalogueButton = React.memo(({ label, isActive, onClick }) => (
    <button
        onClick={onClick}
        className={`btn btn-sm btn-primary ${isActive ? '' : 'btn-outline'}`}
    >
        {label}
    </button>
));

// Composant pour le dropdown des catégories secondaires
const SubCategoryDropdown = React.memo(({ selectedSubCategory, handleSelectedSubCategoryChange, data, selectedCatalogue }) => {
    // Ordre prédéfini pour les catégories physiques
    const physiquesOrder = [
        "Alimentaire",
        "Environnemental",
        "Composants cellulaires",
        "Organique",
        "Pathogène",
        "Pharmaceutique",
        "Sens"
    ];

    // Récupérer les catégories disponibles dans les données
    const availableCategories = [...new Set(data.values.map(item => item.subCategory))];

    // Trier les catégories selon l'ordre défini si le catalogue est "physiques"
    let sortedCategories;
    if (selectedCatalogue === 'physiques') {
        // Filtrer pour ne garder que les catégories disponibles dans l'ordre prédéfini
        sortedCategories = physiquesOrder.filter(category => availableCategories.includes(category));
        // Ajouter les catégories qui seraient dans les données mais pas dans l'ordre prédéfini
        const remainingCategories = availableCategories.filter(category => !physiquesOrder.includes(category));
        sortedCategories = [...sortedCategories, ...remainingCategories];
    } else {
        // Sinon, utiliser l'ordre par défaut (alphabétique ou tel que retourné)
        sortedCategories = availableCategories;
    }

    return (
        <div className="dropdown dropdown-end">
            <div tabIndex={0} role="button" className="btn btn-primary btn-sm btn-outline m-1">
                {selectedSubCategory || 'Catégories Secondaires'}
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 ml-1">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                </svg>
            </div>
            <ul tabIndex={0} className="dropdown-content z-[1] menu p-2 gap-2 shadow bg-base-100 rounded-box w-fit overflow-y-auto">
                <li className=''>
                    <div
                        onClick={() => handleSelectedSubCategoryChange('')}
                        className={selectedSubCategory === '' ? 'bg-primary text-primary-content ' : ''}
                    >
                        Toutes
                    </div>
                </li>
                {sortedCategories.map(subCategory => (
                    <li className='' key={subCategory}>
                        <div
                            onClick={() => handleSelectedSubCategoryChange(subCategory)}
                            className={selectedSubCategory === subCategory ? 'bg-primary text-primary-content ' : ''}
                        >
                            {subCategory}
                        </div>
                    </li>
                ))}
            </ul>
        </div>
    );
});

const AddMixtureTable = ({ width, children }) => {
    const {
        mixturesTableData: data,
        setMixturesTableData: setData,
        setFetchAll,
        pages: serverPages,
        setPages,
        pages,
        searchType,
        setSearchType,
        pagination,
        setPagination,
        totalItemsFetched,
        pageCount,
        setPageCount,
        sorting,
        setSorting,
        globalFilter,
        setGlobalFilter,
        mixturesSelection: selection,
        setMixturesSelection: setSelection,
        handleCustomMixturesFetch,
        handleCatalogueFetch,
        handlePhysiquesMixtureFetch,
        handleEffluencesMixturesFetch,
        handleOthersMixturesFetch,
        API_URL,
        setCapsuleConfiguration,
        capsuleConfiguration,
        defaultConfig,
        defaultCapsule,
        isMixturesLoading: isLoading,
        selectedCatalogue,
        setSelectedCatalogue
    } = useCapsuleConfigurator()

    const [selectedSubCategory, setSelectedSubCategory] = useState(''); // State for selected sub-category
    const [initialSelection, setInitialSelection] = useState({});
    // Nouvelle variable pour déterminer si c'est une nouvelle configuration (contenu vide)
    const isNewConfiguration = useMemo(() => {
        return capsuleConfiguration?.content?.length === 0;
    }, [capsuleConfiguration]);

    const [paginationServer, setPaginationServer] = useState({
        ...data?.pagination,
        fetched: totalItemsFetched,
    });

    const tableRef = useRef(null);

    const [isFirstConfiguration, setIsFirstConfiguration] = useState(false);

    const handleCatalogueClick = useCallback((catalogue) => {
        setSelectedCatalogue(catalogue);

        // Reset le filtre par catégorie secondaire quand on change de catégorie principale
        setSelectedSubCategory('');

        // Reset la pagination et le tri
        setPages([0]);
        setPageCount(1);
        setPagination({
            pageIndex: 0,
            pageSize: pagination.pageSize
        });
        setPaginationServer({
            ...paginationServer,
            pageIndex: 0,
            pageSize: pagination.pageSize
        });

        switch (catalogue) {
            case 'tous':
                handleCatalogueFetch();
                setSorting([{ id: 'ref', desc: false }]);
                break;
            case 'physiques':
                handlePhysiquesMixtureFetch();
                setSorting([{ id: 'ref', desc: false }]);
                break;
            case 'effluences':
                handleEffluencesMixturesFetch();
                setSorting([]);
                break;
            case 'autres':
                handleOthersMixturesFetch();
                setSorting([{ id: 'ref', desc: false }]);
                break;
            case 'custom':
                handleCustomMixturesFetch();
                setSorting([{ id: 'ref', desc: false }]);
                break;
            default:
                break;
        }
    }, [
        handleCatalogueFetch,
        handlePhysiquesMixtureFetch,
        handleEffluencesMixturesFetch,
        handleOthersMixturesFetch,
        handleCustomMixturesFetch,
        setSelectedCatalogue,
        pagination.pageSize,
        paginationServer,
        setPages,
        setPageCount,
        setPagination,
        setPaginationServer,
        setSorting
    ]);


    const columnsDefs = [
        {
            id: 'name',
            accessorFn: row => row.name,
            header: () => <span>Nom</span>,
            cell: info => <span className={`${info.row.original.category === 'Effluences' ? 'text-accent' : ''}`}>{info.getValue()}</span>,
            footer: props => props.column.id,
            filterFn: filterFns.includesString,
            sortingFn: sortingUtils.sortText,
        },
        {
            id: 'subCategory',
            accessorFn: row => row.subCategory,
            header: () => <span>Catégories</span>,
            cell: info => <span>{info.getValue()}</span>,
            footer: props => props.column.id,
            sortingFn: sortingUtils.sortSubCategory,
            filterFn: filterFns.includesString,
            enableHiding: true,
            size: 50
        },
        {
            id: 'defaultDilution.label',
            accessorFn: row => row.defaultDilution.label,
            header: () => <span>Dilution</span>,
            cell: info => <span>{info.getValue()}</span>,
            footer: props => props.column.id,
            sortingFn: sortingUtils.sortText,
            filterFn: filterFns.includesString,
            enableHiding: true,
            size: 50
        },
        {
            id: 'ref',
            accessorFn: row => row.ref,
            header: () => <span>Référence</span>,
            cell: info => <span>{info.getValue()}</span>,
            footer: props => props.column.id,
            sortingFn: sortingUtils.sortText,
            filterFn: filterFns.includesString,
            enableHiding: true,
            size: 50
        },
        {
            id: 'reagents',
            accessorFn: row => row.reagents,
            header: () => <span>Composition</span>,
            cell: (info) => <ReagentsModal mixtureName={info.row.original.name} reagents={info.getValue()} description={info.row.original.description} />,
            footer: props => props.column.id,
            filterFn: filterFns.arrIncludes,
            enableHiding: true,
            enableSorting: false
        },
        {
            id: 'actions',
            accessorFn: row => row.id,
            header: () => <span></span>,
            cell: info => <MixturesRowActions table={info.table} row={info.row} />,
            footer: props => props.column.id,
            enableResizing: false, //disable resizing for just this column
            enableGlobalFilter: false, //disable global filtering for just this column
            size: 12, //starting column size
        }
    ]
    const [columnsVisibility, setColumnsVisibility] = useState(getColumsVisibility([createColumnsWithCheckbox(), ...columnsDefs]))

    const columns = [createColumnsWithCheckbox(), ...columnsDefs]

    const filteredData = useMemo(() => {
        if (!data) return [];
        let returnedData = [...data.values];

        // Filtrer les mélanges custom en fonction du catalogue
        if (selectedCatalogue && selectedCatalogue !== 'custom') {
            returnedData = returnedData.filter(item => !item.private); // Supposant que isCustom est la propriété qui indique si un mélange est custom
        }

        // Apply global filter
        if (globalFilter.value.length > 0 && globalFilter.keys.length > 0) {
            const searchFn = searchType === searchUtils.SEARCH_TYPES.fuzzy ? searchUtils.fuzzySearch : searchUtils.exactSearch;
            returnedData = searchFn(returnedData, globalFilter.value, globalFilter.keys);
            setPaginationServer(prev => ({
                ...prev,
                total: returnedData.length,
                fetched: returnedData.length,
            }));
        }

        // Filter by selected sub-category
        if (selectedSubCategory) {
            returnedData = returnedData.filter(item => item.subCategory === selectedSubCategory);
            setPaginationServer(prev => ({
                ...prev,
                total: returnedData.length,
                fetched: returnedData.length,
            }));
        }

        if (!selectedSubCategory && !globalFilter.value.length) {
            setPaginationServer(prev => ({
                ...prev,
                total: data.pagination.total,
                fetched: totalItemsFetched,
            }));
        }

        if (sorting.length > 0) {
            const sort = sorting[0]
            const { id, desc } = sort
            const sortFn = columnsDefs.find(column => column.id === id)?.sortingFn
            if (sortFn && typeof sortFn === 'function') {
                returnedData.sort((a, b) => {
                    return sortFn(a, b, id, desc)
                })
            }
        }

        const startRow = pagination.pageIndex * pagination.pageSize;
        const endRow = startRow + pagination.pageSize;
        return returnedData.slice(startRow, endRow);
    }, [data, selectedCatalogue, selectedSubCategory, globalFilter, pagination, searchType, sorting]);

    const handleNextPage = useCallback((newPage) => {
        if (newPage !== null)
            setPages((prev) => [newPage, ...prev])
        else
            setPages((prev) => [(pages[0] + 1), ...prev])
    }, [pages, setPages])

    const handleSorting = useCallback((updater) => {
        if (typeof updater !== "function") return;
        const state = updater(sorting);
        setSorting([...state]);
    }, [sorting, setSorting])

    const handleFetchAll = useCallback(() => {
        setFetchAll(true)
    }, [setFetchAll])

    const handleSelectMixtures = (state) => {
        const stateKeys = Object.keys(state).map(id => parseInt(id))
        const selectionKeys = Object.keys(selection).map(id => parseInt(id))
        const addedMixturesIds = stateKeys.reduce((acc, id) => {
            if (!selectionKeys.includes(id)) {
                acc.push(id)
            }
            return acc
        }, [])

        const addedMixtures = data.values.reduce((acc, mixture) => {
            if (addedMixturesIds.includes(mixture.id)) {
                acc.push(mixture)
            }
            return acc
        }, [])

        /* Check if mixtures is in default capsuleConfiguration content from "defaultConfig"
         If it is, add it with the defaultConfig values
         If it is not, add it with the default values */
        const updatedContent = addedMixtures.reduce((acc, mixture) => {
            const capsuleName = defaultCapsule ? defaultCapsule.name : 'Capsule A'
            const formattedMixture = formatMixtureForConfiguration(mixture, capsuleName)
            if (defaultConfig && defaultConfig.content.map(item => item.mixture.id).includes(mixture.id)) {
                const defaultMixture = defaultConfig.content.find(item => item.mixture.id === mixture.id)
                acc.push(defaultMixture)
            } else {
                acc.push(formattedMixture)
            }
            return acc
        }, [])
        setCapsuleConfiguration({
            ...capsuleConfiguration,
            content: mergeMixturesArray(capsuleConfiguration.content, updatedContent)
        })
    }

    const handleRemoveMixtures = (mixtures) => {
        // Remove mixtures from the capsuleConfiguration content
        const updatedContent = capsuleConfiguration.content.reduce((acc, item) => {
            if (!mixtures.map(mixture => mixture.id).includes(item.mixture.id)) {
                acc.push(item)
            }
            return acc
        }, [])
        setCapsuleConfiguration({
            ...capsuleConfiguration,
            content: updatedContent
        })
    }

    const handleSelectedSubCategoryChange = (subCategory) => {
        setSelectedSubCategory(subCategory)
        setSorting([{ id: 'ref', desc: false }])
        setPages([0])
        setPageCount(1)
        setPagination({
            pageIndex: 0,
            pageSize: pagination.pageSize
        })
        setPaginationServer({
            ...paginationServer,
            pageIndex: 0,
            pageSize: pagination.pageSize
        })
    }

    const handleSelectionChange = (updater) => {
        if (typeof updater !== "function") return;
        const state = updater(selection);
        setSelection(state)

        // Find length of the state and selection objects
        // If the length is different, it means that a mixture was added or removed
        const stateLength = Object.keys(state).length
        const selectionLength = Object.keys(selection).length

        if (selectionLength === 0 && stateLength > 0) {
            // First time adding mixtures
            return handleSelectMixtures(state)
        }

        if (selectionLength > 0 && stateLength === 0) {
            // Find the mixture that was removed
            // Includes don't work with objects, so we need to convert the objects to arrays
            const stateKeys = Object.keys(state).map(id => parseInt(id))
            const selectionKeys = Object.keys(selection).map(id => parseInt(id))
            const removedMixturesIds = selectionKeys.reduce((acc, id) => {
                if (!stateKeys.includes(id)) {
                    acc.push(id)
                }
                return acc
            }, [])
            const removedMixtures = data.values.reduce((acc, mixture) => {
                if (removedMixturesIds.includes(mixture.id)) {
                    acc.push(mixture)
                }
                return acc
            }, [])
            handleRemoveMixtures(removedMixtures)
        }

        // Add mixture
        if (stateLength > selectionLength) {
            // Find mixtures that was added, whith the id in the "state" object but not in the "selection" object
            // Includes don't work with objects, so we need to convert the objects to arrays
            return handleSelectMixtures(state)
        }

        // Remove mixture
        if (stateLength < selectionLength) {
            // Find the mixture that was removed
            // Includes don't work with objects, so we need to convert the objects to arrays
            const stateKeys = Object.keys(state).map(id => parseInt(id))
            const selectionKeys = Object.keys(selection).map(id => parseInt(id))

            const removedMixturesIds = selectionKeys.reduce((acc, id) => {
                if (!stateKeys.includes(id)) {
                    acc.push(id)
                }
                return acc
            }, [])
            const removedMixtures = data.values.reduce((acc, mixture) => {
                if (removedMixturesIds.includes(mixture.id)) {
                    acc.push(mixture)
                }
                return acc
            }, [])
            handleRemoveMixtures(removedMixtures)

            return;
        }
    }

    const handlePaginationChange = (updater) => {
        if (typeof updater !== "function") return;
        const state = updater(table.getState().pagination);
        const formattedClientIndexToServer = parseInt(((state.pageIndex) * state.pageSize) / data.pagination.max_per_query) + 1
        if (isArray(serverPages) && !includes(serverPages, formattedClientIndexToServer) && formattedClientIndexToServer > 0) {
            handleNextPage(formattedClientIndexToServer)
        }
        const correctPageCount = getCorrectPageCount(paginationServer.total, state.pageSize);
        setPagination(state);
        setPageCount(correctPageCount > 0 ? correctPageCount : 1);

        // Scroll to top of table when page changes
        if (tableRef.current && capsuleConfiguration.content.length > 0) {
            tableRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }

        // Update paginationServer when pagination changes
        setPaginationServer({
            ...paginationServer,
            pageIndex: state.pageIndex,
        });
    };
    const table = useReactTable(
        {
            data: filteredData,
            columns: columns,
            columnResizeMode: 'onChange',
            columnResizeDirection: 'ltr',
            defaultColumn: {
                size: 150, //starting column size
                minSize: 20, //enforced during column resizing
                maxSize: Number.MAX_SAFE_INTEGER, //enforced during column resizing
            },
            state: {
                sorting,
                pagination,
                rowSelection: selection,
                columnVisibility: columnsVisibility
            },
            onPaginationChange: handlePaginationChange,
            onSortingChange: handleSorting,
            onRowSelectionChange: handleSelectionChange,
            onColumnVisibilityChange: setColumnsVisibility,
            getCoreRowModel: getCoreRowModel(),
            getFilteredRowModel: getFilteredRowModel(),
            manualPagination: true, //turn off client-side pagination,
            manualSorting: true, //turn off client-side sorting
            manualFiltering: true, //turn off client-side filtering
            enableRowSelection: true,
            pageCount: pageCount,
            rowCount: data?.pagination?.total || 0,
            getRowId: row => row.id,
            meta: {
                getRowsByIds: (ids) => {
                    const formattedIds = Object.keys(ids).map(id => parseInt(id))
                    const rows = data.values.filter(row => formattedIds.includes(row.id))
                    return rows
                },
                updateColumn: (rowIndex, columnId, value) => {
                    // Skip page index reset until after next rerender
                    setData(old =>
                        old.map((row, index) => {
                            if (index === rowIndex) {
                                return {
                                    ...old[rowIndex],
                                    [columnId]: value,
                                }
                            }
                            return row
                        })
                    )
                },
                updateFullRow: (rowIndex, updatedRow) => {
                    const updatedData = [...data]
                    updatedData[rowIndex] = updatedRow
                    setData({
                        ...data,
                        values: updatedData
                    });
                },
                removeRow: (rowIndex) => {
                    const updatedData = [...data]
                    const removedRow = updatedData.splice(rowIndex, 1)
                    const index = data.indexOf(removedRow)
                    if (index > -1) {
                        updatedData.splice(index, 1)
                    }
                    setData({
                        ...data,
                        values: updatedData
                    })
                },
                removeBatchRows: (rowIndexes) => {
                    const dataCopy = [...data]
                    const rowToRemove = rowIndexes.map(index => data[index])
                    rowToRemove.forEach(row => {
                        const index = dataCopy.indexOf(row)
                        dataCopy.splice(index, 1)
                    })
                    setData({
                        ...data,
                        values: dataCopy
                    })
                }
            }
        });

    const headers = table.getFlatHeaders();
    const rows = table.getRowModel().rows;

    // Si nous n'avons pas de données au montage, charger les physiques
    useEffect(() => {
        if (!data?.values?.length && selectedCatalogue === 'physiques') {
            handlePhysiquesMixtureFetch();
        }
    }, []);

    useEffect(() => {
        setSorting([{ id: 'ref', desc: false }]); // Set default sorting by 'ref'
    }, []);

    useEffect(() => {

        if (!isLoading && tableRef.current && pagination.pageIndex >= 1) {
            tableRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [isLoading, tableRef.current, pagination.pageIndex]);

    // Mettre à jour initialSelection seulement si ce n'est pas une nouvelle configuration
    useEffect(() => {
        if (!isNewConfiguration && Object.keys(selection).length > 0 && Object.keys(initialSelection).length === 0) {
            setInitialSelection({ ...selection });
        }
    }, [selection, isNewConfiguration]);

    // Mettre à jour cette fonction pour déterminer si c'est la première configuration
    useEffect(() => {
        // Une configuration est considérée comme la première si:
        // - Elle est nouvelle (content.length === 0)
        // - ET aucun mélange n'a été sélectionné précédemment (initialSelection est vide)
        if (isNewConfiguration && Object.keys(initialSelection).length === 0) {
            setIsFirstConfiguration(true);
        }
    }, [isNewConfiguration, initialSelection]);

    if (isLoading) return <LoadingSpinner style='medium' />
    if (!data && !isLoading) return <div>Error Loading Data</div>
    return (
        <div ref={tableRef} className="relative space-y-2">
            {children && React.Children.map(children, (child) => {
                return React.cloneElement(child, { setData, selection, setSelection, table });
            })}
            <div className='flex flex-col gap-4 mb-4'>
                {/* First row: Search and dropdown */}
                <div className='flex flex-row flex-wrap justify-between items-center'>
                    <Search
                        columns={columnsDefs}
                        globalFilter={globalFilter}
                        setGlobalFilter={setGlobalFilter}
                        setSearchType={setSearchType}
                        searchType={searchType}
                        columnsVisibility={columnsVisibility}
                        setColumnsVisibility={setColumnsVisibility}
                    />

                    <SubCategoryDropdown
                        selectedSubCategory={selectedSubCategory}
                        handleSelectedSubCategoryChange={handleSelectedSubCategoryChange}
                        data={data}
                        selectedCatalogue={selectedCatalogue}
                    />
                </div>

                {/* Second row: Catalogue buttons */}
                <div className='flex flex-row flex-wrap gap-2 justify-between'>
                    <div className='flex flex-row flex-wrap gap-2'>
                        <CatalogueButton
                            label="Tous"
                            isActive={selectedCatalogue === 'tous'}
                            onClick={() => handleCatalogueClick('tous')}
                        />
                        <CatalogueButton
                            label="Physiques"
                            isActive={selectedCatalogue === 'physiques'}
                            onClick={() => handleCatalogueClick('physiques')}
                        />
                        <CatalogueButton
                            label="Effluences"
                            isActive={selectedCatalogue === 'effluences'}
                            onClick={() => handleCatalogueClick('effluences')}
                        />
                        <CatalogueButton
                            label="Autres"
                            isActive={selectedCatalogue === 'autres'}
                            onClick={() => handleCatalogueClick('autres')}
                        />
                        <CatalogueButton
                            label="Personnalisés"
                            isActive={selectedCatalogue === 'custom'}
                            onClick={() => handleCatalogueClick('custom')}
                        />
                    </div>
                    {
                        filteredData.length > 0 &&
                        <Pagination
                            server={paginationServer}
                            client={pagination}
                            table={table}
                            handleFetchAll={handleFetchAll}
                        />
                    }
                </div>
            </div>

            <div className="overflow-hidden">
                <table style={{ width: width ? width : table.getCenterTotalSize() }} className="table table-sm border-spacing-0">
                    <thead>
                        <tr>
                            {headers.map((header) => (
                                <TableHeader key={header.id} header={header} />
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => (
                            <TableRow
                                key={row.id}
                                row={row}
                                selection={selection}
                                initialSelection={initialSelection}
                                isFirstConfiguration={isFirstConfiguration}
                            />
                        ))}
                    </tbody>
                </table>
            </div>
            <div className='flex flex-row flex-wrap gap-2 justify-between'>
                <p className='text-xs text-gray-500'>{filteredData.length} résultats</p>
                {
                    filteredData.length > 0 &&
                    <Pagination
                        server={paginationServer}
                        client={pagination}
                        table={table}
                        handleFetchAll={handleFetchAll}
                    />
                }
            </div>
        </div>
    )
}

export default React.memo(AddMixtureTable)
